// The shape of an extracted presentation, and the pure rules that govern it: how a
// slide or a shape is addressed, which shapes can hold text at all, and how much of a
// deck is allowed into a prompt.
//
// Nothing in this file touches Office.js. That is deliberate rather than tidy: it means
// every rule here is unit-testable without a live PowerPoint, and that none of it can
// throw before Office.onReady.

/**
 * The addressing scheme, and why it is not Word's.
 *
 * Word synthesises paragraph IDs (pA, pB, ...) from position and re-derives them on
 * every extract, so an edit that shifts the document silently invalidates any ID the
 * model is still holding. PowerPoint does not force that trade, because it hands out
 * identifiers of its own:
 *
 *   s3        -- slide 3, counted from 1 so it matches the number PowerPoint itself
 *                shows in the thumbnail rail and the user says out loud
 *   s3/sh17   -- the shape whose PowerPoint id is 17, currently sitting on slide 3
 *
 * The half after the slash is an identity, not a position: `shape.id` comes from the
 * host, so the resolver can still find that shape after slides are added, deleted or
 * reordered, and the slide number in front of it is only a readable hint. This matters
 * more here than it looks, because `slide.shapes` carries no documented ordering
 * guarantee at all (unlike `presentation.slides`, which explicitly does) -- a purely
 * positional shape address would be unsound even without any edits.
 *
 * The honest caveat: Microsoft documents `Shape.id` as "the unique ID of the shape" and
 * makes no promise about it surviving a save and reopen. That is enough for us. The
 * deck is re-extracted at the start of every chat turn, so an ID only has to stay valid
 * for the length of one turn, which is exactly the window in which our own edits are
 * shifting things around.
 */
export const POWERPOINT_SEGMENT_ID_PATTERN = "s\\d+(?:/sh[^>\\s]+)?";

/** `s3` for the third slide. Takes the zero-based position, emits the 1-based number. */
export const slideSegmentId = (slideIndex: number): string => `s${slideIndex + 1}`;

/** `s3/sh17` -- the slide is positional, the shape is PowerPoint's own id. */
export const shapeSegmentId = (slideIndex: number, shapeId: string): string =>
  `${slideSegmentId(slideIndex)}/sh${shapeId}`;

export interface ParsedSegmentId {
  /** 1-based, as written in the ID. */
  slideNumber: number;
  /** Absent for a slide-level address. */
  shapeId?: string;
}

/** Returns null for anything that is not one of our IDs, including a bare number. */
export const parseSegmentId = (segmentId: string): ParsedSegmentId | null => {
  const match = /^s(\d+)(?:\/sh(.+))?$/.exec(segmentId.trim());
  if (!match) return null;

  const slideNumber = parseInt(match[1], 10);
  if (!Number.isFinite(slideNumber) || slideNumber < 1) return null;

  return match[2] ? { slideNumber, shapeId: match[2] } : { slideNumber };
};

/**
 * Shape types we will ask for a text frame from.
 *
 * `Shape.textFrame` is not optional-shaped below PowerPointApi 1.10: on a shape that
 * has no text frame it throws InvalidArgument, and because loads are batched that one
 * bad shape takes the whole `context.sync()` -- and therefore the whole extraction --
 * down with it. `getTextFrameOrNullObject` would solve it cleanly but arrived at 1.10,
 * far above our floor. So we ask only where we are confident of the answer, and treat
 * anything unrecognised as text-free rather than risking the batch. An allowlist, not a
 * denylist, because a shape type we have never heard of should cost us nothing.
 *
 * These are the *values* of PowerPoint.ShapeType, which are PascalCase even though the
 * enum members are camelCase -- `ShapeType.geometricShape === "GeometricShape"`. They
 * are written as string literals rather than read off the enum for the reason set out
 * in ../shared/compressedBytesSupport.ts: module scope runs before Office.onReady and
 * under Jest there is no PowerPoint global at all.
 */
const TEXT_CAPABLE_SHAPE_TYPES: ReadonlySet<string> = new Set([
  "TextBox",
  "Placeholder",
  "GeometricShape",
  "Callout",
  "Freeform",
  "Line",
]);

export const shapeCanHoldText = (shapeType: string): boolean =>
  TEXT_CAPABLE_SHAPE_TYPES.has(shapeType);

/** The one shape type whose text lives behind `getTable()` instead of a text frame. */
export const TABLE_SHAPE_TYPE = "Table";

/**
 * Makes shape text safe to show the model, WITHOUT changing its length.
 *
 * PowerPoint does not return `\n` for a line break. A paragraph break comes back as
 * `\r` and a soft break as `\v` (vertical tab) -- undocumented, but the shipped Office
 * sample for `getSelectedTextRange` strips exactly those three characters, and a shape's
 * text genuinely spans paragraphs. Left alone they reach the model as escaped noise.
 *
 * The length-preserving part is the whole point, and is why this does not collapse a
 * `\r\n` pair into one character the way a normal newline tidy-up would. Character
 * offsets into the normalised string have to remain valid offsets into the live text,
 * because search_and_replace_in_shape finds the model's `find` string in the normalised
 * text and then hands that index straight to `TextRange.getSubstring`. Collapse
 * anything and every edit after the first line break lands in the wrong place. The cost
 * is a stray blank line in the payload on any host that really does emit `\r\n`, which
 * is cosmetic and preferable.
 */
export const normaliseShapeText = (text: string): string => text.replace(/[\r\v]/g, "\n");

// ── The extracted model ─────────────────────────────────────────────────────

export interface DeckTableModel {
  rows: number;
  columns: number;
  /** Row-major cell text, as `Table.values` reports it. */
  values: string[][];
}

export interface DeckShapeModel {
  segmentId: string;
  /** PowerPoint's own id, repeated here so the model never has to parse an ID apart. */
  shapeId: string;
  name: string;
  type: string;
  /** Absent when the shape holds no text, or when its text was withheld. */
  text?: string;
  /** The text is the beginning of a longer body. Ask for the rest with read_slide. */
  textTruncated?: true;
  /** The text exists but did not fit the prompt budget. read_slide will return it. */
  textWithheld?: true;
  table?: DeckTableModel;
}

export interface DeckSlideModel {
  segmentId: string;
  /** Needed verbatim by delete_slide, which will not act on a slide number alone. */
  slideId: string;
  layout?: string;
  shapes: DeckShapeModel[];
  /** This slide's shape text could not be read; its shapes are listed without text. */
  textUnreadable?: true;
}

export interface DeckModel {
  /** Slides in the presentation, which is not always `slides.length` -- see notice. */
  slideCount: number;
  slides: DeckSlideModel[];
  /** Layout names available for add_slide, from the presentation's first master. */
  layouts?: string[];
  /**
   * A plain-English statement of everything missing from this payload. Present only
   * when something is missing, so its mere presence is the signal.
   */
  notice?: string;
}

// ── Prompt budget ───────────────────────────────────────────────────────────

export interface DeckBudget {
  /** Slides beyond this are not listed at all. */
  maxSlides: number;
  /** A single shape's text is cut to this many characters. */
  maxShapeTextChars: number;
  /** Once this much shape text has been admitted, later shapes contribute none. */
  maxTotalTextChars: number;
}

/**
 * Deliberately generous. A deck is not a spreadsheet: sixty slides of bullet points is
 * a Word-sized document, so the default is to send the whole thing and let the model
 * see the deck as the user sees it. These limits exist for the pathological deck -- the
 * 400-slide training pack, the shape holding a pasted contract -- where the alternative
 * is a request that fails or silently drops the end of the presentation.
 */
export const DEFAULT_DECK_BUDGET: DeckBudget = {
  maxSlides: 200,
  maxShapeTextChars: 2000,
  maxTotalTextChars: 120000,
};

/** What read_slide uses: one slide, nothing held back. */
export const SLIDE_DETAIL_BUDGET: DeckBudget = {
  maxSlides: 1,
  maxShapeTextChars: 100000,
  maxTotalTextChars: 100000,
};

export interface TextAdmission {
  /** The text to publish, absent when there is none or when it was withheld. */
  text?: string;
  truncated: boolean;
  withheld: boolean;
  /** Characters this shape consumed of the total budget. */
  charsSpent: number;
}

/**
 * Decides how much of one shape's text goes into the payload.
 *
 * The two limits do different jobs. `maxShapeTextChars` stops one essay-in-a-textbox
 * from crowding out the other 199 slides; `maxTotalTextChars` is the hard stop on the
 * whole payload. Crossing the total does not truncate mid-shape -- the shape is
 * withheld outright -- because a body of text cut at an arbitrary character is worse
 * than an explicit "this was not included": the model can act on the second and cannot
 * detect the first.
 */
export const admitText = (
  rawText: string,
  charsAlreadySpent: number,
  budget: DeckBudget
): TextAdmission => {
  if (rawText.trim().length === 0) {
    return { truncated: false, withheld: false, charsSpent: 0 };
  }

  if (charsAlreadySpent >= budget.maxTotalTextChars) {
    return { truncated: false, withheld: true, charsSpent: 0 };
  }

  const truncated = rawText.length > budget.maxShapeTextChars;
  const text = truncated ? rawText.substring(0, budget.maxShapeTextChars) : rawText;
  return { text, truncated, withheld: false, charsSpent: text.length };
};

// ── Saying what is missing ──────────────────────────────────────────────────

export interface DeckElision {
  slidesOmitted: number;
  shapesTruncated: number;
  shapesWithheld: number;
  /** 1-based slide numbers whose shape text the host refused to hand over. */
  slidesUnreadable: number[];
  /** Table contents need PowerPointApi 1.8, which this host does not have. */
  tablesUnavailable: boolean;
}

export const NO_ELISION: DeckElision = {
  slidesOmitted: 0,
  shapesTruncated: 0,
  shapesWithheld: 0,
  slidesUnreadable: [],
  tablesUnavailable: false,
};

/**
 * Turns the elision counters into a sentence for the payload, or undefined when nothing
 * was left out.
 *
 * The point of writing it out in prose, inside the document state the model is reading,
 * is that a model cannot notice an absence. A silently shortened deck reads exactly
 * like a short deck, and the answer that follows is confidently wrong. Naming the gap
 * -- and naming the tool that closes it -- turns that into a question the model can
 * ask.
 */
export const describeDeckElision = (elision: DeckElision): string | undefined => {
  const parts: string[] = [];

  if (elision.slidesOmitted > 0) {
    parts.push(
      `${elision.slidesOmitted} slide(s) at the end of the presentation are not listed here.`
    );
  }
  if (elision.shapesTruncated > 0) {
    parts.push(
      `${elision.shapesTruncated} shape(s) show only the start of their text (marked textTruncated).`
    );
  }
  if (elision.shapesWithheld > 0) {
    parts.push(
      `${elision.shapesWithheld} shape(s) have text that is not included here at all (marked textWithheld).`
    );
  }
  if (elision.slidesUnreadable.length > 0) {
    parts.push(
      `PowerPoint would not return the shape text on slide(s) ${elision.slidesUnreadable.join(", ")}; their shapes are listed without it.`
    );
  }
  if (elision.tablesUnavailable) {
    parts.push(
      "This version of PowerPoint cannot report table contents (it needs PowerPointApi 1.8), so tables appear as shapes with no cells and cannot be edited."
    );
  }

  if (parts.length === 0) return undefined;

  return `This is a PARTIAL view of the presentation. ${parts.join(" ")} Call read_slide for any slide you need in full, and say so rather than guessing if the answer depends on something listed as missing.`;
};
